import { useEffect, useState } from 'react';
import { AlertTriangle, Check, FileText, FolderOpen, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatUsd, fmtDateOnly } from '../lib/format';
import { uploadFile } from '../lib/upload';

type Service = {
  id: string;
  service_type: string;
  payee_kind: string;
  vendor_name: string | null;
  performed_date: string | null;
  amount_cents: number | null;
  invoice_key: string | null;
  status: string;
  billed_cents: number | null;
  insurance_paid_cents: number | null;
  needs_estimate: boolean;
};

const TYPES = ['tarp', 'mitigation', 'tree_removal', 'board_up', 'water_extraction', 'roof_emergency', 'other'];
const LABEL: Record<string, string> = {
  tarp: 'Tarp', mitigation: 'Mitigation', tree_removal: 'Tree Removal', board_up: 'Board Up',
  water_extraction: 'Water Extraction', roof_emergency: 'Roof Emergency Repair', other: 'Other',
};
const dollars = (c: number | null) => (c == null ? '' : String(c / 100));
const toCents = (s: string) => { const n = parseFloat(s); return Number.isFinite(n) ? Math.round(n * 100) : null; };

/**
 * Services Performed: trabajos de emergencia/mitigación en la propiedad. Compartido
 * con Estimating (recordar incluir) y Finance (facturado vs pagado por el seguro).
 */
export function ClaimServices({ claimId, contractorName }: { claimId: string; contractorName?: string | null }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<Service[]>([]);
  const [type, setType] = useState('tarp');
  const [payeeKind, setPayeeKind] = useState('contractor');
  const [otherName, setOtherName] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [needsEstimate, setNeedsEstimate] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setRows((await api.get<{ services: Service[] }>(`/api/claims/${claimId}/services`)).services); } catch { /* ignore */ }
  }
  useEffect(() => { load(); }, [claimId]);

  // Nombre resuelto del payee según el tipo elegido.
  function payeeName(kind: string): string | null {
    if (kind === 'proactive') return 'Proactive';
    if (kind === 'contractor') return contractorName ?? 'Assigned contractor';
    return otherName.trim() || null;
  }

  async function add() {
    if (!type) return;
    if (payeeKind === 'other' && !otherName.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/claims/${claimId}/services`, {
        serviceType: type, payeeKind, vendorName: payeeName(payeeKind), performedDate: date || null, amountCents: toCents(amount), needsEstimate,
      });
      setOtherName(''); setDate(''); setAmount(''); setPayeeKind('contractor'); setNeedsEstimate(false);
      await load();
    } finally { setBusy(false); }
  }
  async function patch(id: string, body: Record<string, unknown>) {
    try { const r = await api.patch<Service>(`/api/services/${id}`, body); setRows((rs) => rs.map((x) => (x.id === id ? r : x))); } catch { /* ignore */ }
  }
  async function del(id: string) {
    if (!window.confirm(t('svc.deleteConfirm'))) return;
    try { await api.del(`/api/services/${id}`); await load(); } catch { /* ignore */ }
  }
  async function attachInvoice(id: string, file: File) {
    try { const key = await uploadFile(file, 'invoices'); await patch(id, { invoiceKey: key, status: 'invoiced' }); } catch { /* ignore */ }
  }
  // Elegir la factura de los ARCHIVOS del claim (sin re-subir el mismo PDF).
  const [pickFor, setPickFor] = useState<string | null>(null);
  const [claimFiles, setClaimFiles] = useState<Array<{ id: string; file_name: string | null; file_key: string; doc_type: string | null }> | null>(null);
  async function loadClaimFiles() {
    if (claimFiles) return;
    try { setClaimFiles((await api.get<{ files: typeof claimFiles }>(`/api/claims/${claimId}/files`)).files ?? []); }
    catch { setClaimFiles([]); }
  }
  async function attachExisting(id: string, fileKey: string) {
    await patch(id, { invoiceKey: fileKey, status: 'invoiced' });
    setPickFor(null);
  }
  async function openInvoice(key: string) {
    try { const r = await api.post<{ url: string }>(`/api/uploads/view-url`, { key }); window.open(r.url, '_blank', 'noopener'); }
    catch { /* ignore */ }
  }

  const money = (id: string, field: string, val: number | null) => (
    <input
      className="input" style={{ width: 100, fontFamily: 'var(--font-mono)' }} defaultValue={dollars(val)} placeholder="$"
      onBlur={(e) => { const c = e.target.value.trim() === '' ? null : toCents(e.target.value); patch(id, { [field]: c }); }}
    />
  );

  return (
    <div className="card">
      <h2 className="card-title">{t('svc.title')}</h2>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <select className="input" style={{ width: 170 }} value={type} onChange={(e) => setType(e.target.value)}>
          {TYPES.map((tp) => <option key={tp} value={tp}>{LABEL[tp]}</option>)}
        </select>
        <select className="input" style={{ width: 180 }} value={payeeKind} onChange={(e) => setPayeeKind(e.target.value)} title={t('svc.payee')}>
          <option value="contractor">{contractorName ? `${t('svc.contractor')}: ${contractorName}` : t('svc.contractor')}</option>
          <option value="proactive">Proactive</option>
          <option value="other">{t('svc.other')}</option>
        </select>
        {payeeKind === 'other' && (
          <input className="input" style={{ width: 170 }} placeholder={t('svc.otherName')} value={otherName} onChange={(e) => setOtherName(e.target.value)} />
        )}
        <input className="input" type="date" style={{ width: 150 }} value={date} onChange={(e) => setDate(e.target.value)} />
        <input className="input" style={{ width: 110 }} placeholder={t('svc.amount')} value={amount} onChange={(e) => setAmount(e.target.value)} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-sm)' }} title={t('svc.needsEstimateHelp')}>
          <input type="checkbox" checked={needsEstimate} onChange={(e) => setNeedsEstimate(e.target.checked)} />
          {t('svc.needsEstimate')}
        </label>
        <button className="btn" disabled={busy} onClick={add}>+ {t('svc.add')}</button>
      </div>

      {rows.length === 0 ? (
        <div className="muted">{t('svc.empty')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('svc.type')}</th><th>{t('svc.vendor')}</th><th>{t('svc.date')}</th>
                <th className="num">{t('svc.amount')}</th><th>{t('svc.invoice')}</th>
                <th className="num">{t('svc.billed')}</th><th className="num">{t('svc.insPaid')}</th>
                <th className="num">{t('svc.diff')}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const diff = (s.billed_cents ?? 0) - (s.insurance_paid_cents ?? 0);
                return (
                  <tr key={s.id}>
                    <td><span className="badge teal">{LABEL[s.service_type] || s.service_type}</span></td>
                    <td>
                      {s.vendor_name || '—'}
                      {s.payee_kind === 'proactive' && <span className="badge gray" style={{ marginLeft: 6, fontSize: 'var(--fs-2xs)' }}>{t('svc.toProactive')}</span>}
                      {s.payee_kind === 'other' && <span className="badge red" style={{ marginLeft: 6, fontSize: 'var(--fs-2xs)' }}><AlertTriangle size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('svc.toOther')}</span>}
                      {/* El estado se lee SIN ambigüedad (feedback 2026-07-29: el badge gris
                          decía "Needs our estimate" aunque NO estuviera marcado y parecía que
                          el sistema lo había activado solo). Marcado = badge afirmativo;
                          sin marcar = acción con "+ …?" que claramente es un botón. */}
                      {s.needs_estimate ? (
                        <button
                          className="badge teal"
                          style={{ marginLeft: 6, fontSize: 'var(--fs-2xs)', cursor: 'pointer', border: 'none' }}
                          title={t('svc.needsEstimateOffHelp')}
                          onClick={() => patch(s.id, { needsEstimate: false })}
                        >
                          <Check size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('svc.needsEstimate')} <X size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
                        </button>
                      ) : (
                        <button
                          className="btn ghost sm"
                          style={{ marginLeft: 6, fontSize: 'var(--fs-2xs)', padding: '0 6px', color: 'var(--muted)' }}
                          title={t('svc.needsEstimateHelp')}
                          onClick={() => patch(s.id, { needsEstimate: true })}
                        >
                          + {t('svc.needsEstimateAsk')}
                        </button>
                      )}
                    </td>
                    <td className="muted">{fmtDateOnly(s.performed_date) ?? '—'}</td>
                    <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{s.amount_cents != null ? formatUsd(s.amount_cents) : '—'}</td>
                    <td>
                      {s.invoice_key ? (
                        <button className="btn ghost sm" onClick={() => openInvoice(s.invoice_key!)}><FileText size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('svc.viewInvoice')}</button>
                      ) : (
                        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) attachInvoice(s.id, f); }}>
                          <label className="btn ghost sm" style={{ cursor: 'pointer', color: 'var(--red)' }}>
                            {t('svc.pendingInvoice')}
                            <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) attachInvoice(s.id, f); }} />
                          </label>
                          {/* O ELEGIR un archivo que YA está en el claim (feedback 2026-07-29:
                              "el file del invoice ya lo tengo en mis files") — sin re-subir. */}
                          <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} title={t('svc.fromFilesHelp')}
                            onClick={() => { setPickFor(pickFor === s.id ? null : s.id); void loadClaimFiles(); }}>
                            <FolderOpen size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('svc.fromFiles')}
                          </button>
                          {pickFor === s.id && (
                            <select className="input sm" style={{ maxWidth: 240 }} defaultValue=""
                              onChange={(e) => { if (e.target.value) { void attachExisting(s.id, e.target.value); } }}>
                              <option value="">{claimFiles ? t('svc.pickFile') : t('common.loadingDots')}</option>
                              {(claimFiles ?? []).map((f) => (
                                <option key={f.id} value={f.file_key}>{f.file_name || f.file_key}{f.doc_type ? ` · ${f.doc_type}` : ''}</option>
                              ))}
                            </select>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="num">{money(s.id, 'billedCents', s.billed_cents)}</td>
                    <td className="num">{money(s.id, 'insurancePaidCents', s.insurance_paid_cents)}</td>
                    <td className="num" style={{ color: diff > 0 ? 'var(--red)' : 'var(--green)', fontFamily: 'var(--font-mono)' }}>{formatUsd(diff)}</td>
                    <td><button className="btn ghost sm" onClick={() => del(s.id)}><X size={14} strokeWidth={1.75} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
